// Usage page (Phase 5.4). One GET to /v1/usage drives four panels:
//   • Period totals — runs · tokens · cost for the current Stripe window
//   • Plan meter — used / cap bars for messages and tokens; hides when the
//     tenant has no subscription (limits === null)
//   • Daily cost line chart (Recharts) — period buckets backfilled with zeros
//     so a quiet day reads as a flat line, not a missing point
//   • Per-agent breakdown — sorted by cost desc, runs/tokens/cost columns
//
// We rely on a single endpoint rather than four because the Stripe period is
// the same axis for all panels — splitting the read would surface skew if
// the period rolled mid-render.

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { RotateCcw } from 'lucide-react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { UsageBucket, UsageByAgent, UsageSummary } from '@cortex/shared';
import { useApiClient } from '../../lib/providers.js';
import { usageApi, USAGE_QUERY_KEY } from '../../lib/api/usage.js';
import { backfillSeries, shortDay } from '../../lib/usage-helpers.js';
import { formatUsd } from '../../lib/format-money.js';
import { formatDate } from '../../lib/format-date.js';
import { Badge } from '../../components/ui/badge.js';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card.js';
import { ErrorRetry } from '../../components/ui/error-retry.js';
import { PageHeader } from '../../components/ui/page-header.js';
import { Skeleton } from '../../components/ui/skeleton.js';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table.js';
import { useTheme } from '../../lib/theme.js';
import { cn } from '../../lib/utils.js';
import { Button } from '../../components/ui/button.js';
import { ApiError } from '../../lib/api-client.js';
import { useResumeSubscription } from '../../lib/api/billing.js';
import { deriveSubscriptionState } from '../../lib/subscription-state.js';

export function UsagePage() {
  const api = useApiClient();
  const summary = useQuery({
    queryKey: USAGE_QUERY_KEY,
    queryFn: () => usageApi.summary(api),
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Usage"
        description={
          <>
            Tokens, messages, and cost for the current billing window. Sourced from{' '}
            <code className="font-mono text-xs">ai_call_logs</code> — same numbers the daily
            reconciliation job ships to Stripe.
          </>
        }
      />

      {summary.isLoading && <UsageSkeleton />}
      {summary.isError && (
        <ErrorRetry
          title="Could not load usage"
          error={summary.error}
          onRetry={() => void summary.refetch()}
        />
      )}
      {summary.data && <UsageBody summary={summary.data} />}
    </div>
  );
}

function UsageBody({ summary }: { summary: UsageSummary }) {
  return (
    <div className="space-y-6">
      <SubscriptionAlert summary={summary} />
      <PlanBanner summary={summary} />
      <TotalsRow summary={summary} />
      {summary.limits && <PlanMeter summary={summary} />}
      <CostChart
        series={summary.series}
        periodStart={summary.plan.currentPeriodStart}
        periodEnd={summary.plan.currentPeriodEnd}
      />
      <AgentBreakdown rows={summary.byAgent} />
    </div>
  );
}

// Inline alert above the plan banner when the subscription needs attention.
// Frozen continues to be shown on the PlanBanner badge — that's the existing
// State C surface (the alert here is for canceled / scheduled-cancel where
// the user can act without going to /billing).
function SubscriptionAlert({ summary }: { summary: UsageSummary }) {
  const state = deriveSubscriptionState(summary.plan);
  if (state === 'none' || state === 'past-due') return null;

  const cancelDate = summary.plan.cancelAt ? formatDate(summary.plan.cancelAt) : null;
  const message =
    state === 'canceled'
      ? cancelDate
        ? `Service paused — subscription ended on ${cancelDate}. Reactivate to restore access; your data is preserved.`
        : 'Service paused. Reactivate to restore access; your data is preserved.'
      : cancelDate
        ? `Subscription ends on ${cancelDate}. After that, falls to Free tier with reduced limits — data preserved. Resume to keep current plan.`
        : 'Subscription is scheduled to end. After that, falls to Free tier with reduced limits — data preserved. Resume to keep current plan.';

  return (
    <div
      role="alert"
      className={cn(
        'flex flex-wrap items-center gap-3 rounded-md border px-4 py-3 text-sm',
        state === 'canceled'
          ? 'border-destructive/30 bg-destructive/10 text-destructive'
          : 'border-warning/30 bg-warning/10 text-warning-foreground',
      )}
    >
      <p className="min-w-0 flex-1">{message}</p>
      <ResumeInline state={state} />
    </div>
  );
}

function ResumeInline({ state }: { state: 'canceled' | 'scheduled-cancel' }) {
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const resume = useResumeSubscription();

  const onResume = () => {
    setError(null);
    resume.mutate(undefined, {
      onSuccess: () => { setConfirming(false); },
      onError: (err) => {
        if (err instanceof ApiError && err.status === 404) {
          setError('Stripe billing not configured.');
          return;
        }
        if (err instanceof ApiError && (err.status === 402 || err.status === 409)) {
          setError('State changed — refresh.');
          return;
        }
        setError(err instanceof Error ? err.message : 'Could not resume.');
      },
    });
  };

  if (!confirming) {
    return (
      <Button
        size="sm"
        variant={state === 'canceled' ? 'destructive' : 'default'}
        onClick={() => { setConfirming(true); }}
      >
        <RotateCcw aria-hidden />
        {state === 'canceled' ? 'Reactivate' : 'Continue'}
      </Button>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button size="sm" variant="default" onClick={onResume} disabled={resume.isPending}>
        {resume.isPending ? 'Resuming…' : 'Confirm'}
      </Button>
      <Button
        size="sm"
        variant="ghost"
        onClick={() => { setConfirming(false); }}
        disabled={resume.isPending}
      >
        Cancel
      </Button>
      {error && (
        <span role="alert" className="text-xs text-destructive">
          {error}
        </span>
      )}
    </div>
  );
}

function UsageSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-12 w-full rounded-md" />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={i}>
            <CardHeader className="pb-2">
              <Skeleton className="h-3 w-20" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-24" />
            </CardContent>
          </Card>
        ))}
      </div>
      <Card>
        <CardHeader className="space-y-2">
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-4 w-72" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-64 w-full" />
        </CardContent>
      </Card>
    </div>
  );
}

function PlanBanner({ summary }: { summary: UsageSummary }) {
  const { plan } = summary;
  const isNoSub = plan.code === 'no_subscription';
  const start = formatDate(plan.currentPeriodStart);
  const end = formatDate(plan.currentPeriodEnd);
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-md border bg-card px-4 py-3 text-sm shadow-sm">
      <div className="flex items-center gap-2">
        <span className="text-muted-foreground">Plan</span>
        <Badge variant="default" className="capitalize">
          {plan.code}
        </Badge>
      </div>
      <span className="text-muted-foreground/40" aria-hidden>·</span>
      <span className="text-muted-foreground">
        Period {start} → {end}
      </span>
      {isNoSub && (
        <Badge variant="warning" className="ml-auto">
          no subscription · usage shown for current month
        </Badge>
      )}
      {plan.frozen && (
        <Badge variant="destructive" role="status" className="ml-auto">
          frozen{plan.frozenReason ? ` · ${plan.frozenReason}` : ''}
        </Badge>
      )}
    </div>
  );
}

function TotalsRow({ summary }: { summary: UsageSummary }) {
  const { period } = summary;
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <StatCard label="Runs" value={period.runs.toLocaleString()} />
      <StatCard label="Input tokens" value={period.inputTokens.toLocaleString()} />
      <StatCard label="Output tokens" value={period.outputTokens.toLocaleString()} />
      <StatCard label="Cost" value={formatUsd(period.costUsd)} accent />
    </div>
  );
}

function StatCard({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardDescription className="text-xs uppercase tracking-wide">{label}</CardDescription>
      </CardHeader>
      <CardContent>
        <p className={cn('text-3xl font-semibold tabular-nums', accent && 'text-primary')}>
          {value}
        </p>
      </CardContent>
    </Card>
  );
}

function PlanMeter({ summary }: { summary: UsageSummary }) {
  const { period, limits } = summary;
  if (!limits) return <></>;
  const totalTokens = period.inputTokens + period.outputTokens;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Plan meter</CardTitle>
        <CardDescription>
          Usage against the caps on your current plan. Bars turn amber at 80% and red at 100%.
          Once a cap is hit new chats are rejected; the chat that crosses the cap is allowed to
          finish, so usage may settle slightly above 100%.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <MeterRow label="Messages" used={period.messages} cap={limits.monthlyMessageCap} />
        <MeterRow label="Tokens" used={totalTokens} cap={limits.monthlyTokenCap} />
      </CardContent>
    </Card>
  );
}

function MeterRow({
  label,
  used,
  cap,
}: {
  label: string;
  used: number;
  cap: number | null;
}) {
  if (cap === null) {
    return (
      <div>
        <div className="mb-1.5 flex items-baseline justify-between text-sm">
          <span className="font-medium">{label}</span>
          <span className="text-muted-foreground tabular-nums">
            {used.toLocaleString()} · unlimited
          </span>
        </div>
        <div className="h-2 rounded-full bg-muted" aria-hidden />
      </div>
    );
  }
  const pct = cap === 0 ? 0 : Math.min(100, (used / cap) * 100);
  const tone =
    pct >= 100 ? 'bg-destructive' : pct >= 80 ? 'bg-warning' : 'bg-success';
  return (
    <div>
      <div className="mb-1.5 flex items-baseline justify-between text-sm">
        <span className="font-medium">{label}</span>
        <span className="tabular-nums text-muted-foreground">
          {used.toLocaleString()} / {cap.toLocaleString()} ({pct.toFixed(0)}%)
        </span>
      </div>
      <div
        role="progressbar"
        aria-label={`${label} usage`}
        aria-valuenow={Math.round(pct)}
        aria-valuemin={0}
        aria-valuemax={100}
        className="h-2 overflow-hidden rounded-full bg-muted"
      >
        <div
          className={cn('h-full rounded-full transition-all', tone)}
          style={{ width: `${String(pct)}%` }}
        />
      </div>
    </div>
  );
}

function CostChart({
  series,
  periodStart,
  periodEnd,
}: {
  series: UsageBucket[];
  periodStart: string;
  periodEnd: string;
}) {
  const { resolvedTheme } = useTheme();
  const data = useMemo(
    () => backfillSeries(series, periodStart, periodEnd),
    [series, periodStart, periodEnd],
  );
  const isDark = resolvedTheme === 'dark';
  const gridColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
  const textColor = isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)';

  return (
    <Card>
      <CardHeader>
        <CardTitle>Daily cost</CardTitle>
        <CardDescription>
          USD spent per UTC day across the current billing window. Empty days are filled with
          zero so a flat line means &ldquo;no traffic&rdquo;, not &ldquo;data missing&rdquo;.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {data.length === 0 ? (
          <p className="text-sm text-muted-foreground">No usage in this period yet.</p>
        ) : (
          // Numeric height (Tailwind h-64 = 256px) avoids the
          // "width(-1) and height(-1)" Recharts warning that fires under
          // StrictMode when both dimensions are percentage and the
          // parent hasn't laid out yet. Width stays responsive.
          <div data-testid="usage-cost-chart" className="w-full">
            <ResponsiveContainer width="100%" height={256}>
              <LineChart data={data} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                <CartesianGrid stroke={gridColor} vertical={false} />
                <XAxis
                  dataKey="bucket"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  stroke={textColor}
                  tickFormatter={(v: string) => shortDay(v)}
                />
                <YAxis
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  stroke={textColor}
                  width={56}
                  tickFormatter={(v: number) => `$${v.toFixed(2)}`}
                />
                <RechartsTooltip
                  contentStyle={{
                    background: 'hsl(var(--popover))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: 6,
                    fontSize: 12,
                    color: 'hsl(var(--popover-foreground))',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                  }}
                  labelStyle={{ color: 'hsl(var(--muted-foreground))' }}
                  labelFormatter={(label) => (typeof label === 'string' ? shortDay(label) : String(label))}
                  formatter={(value) => [
                    `$${typeof value === 'number' ? value.toFixed(4) : String(value)}`,
                    'Cost',
                  ]}
                />
                <Line
                  type="monotone"
                  dataKey="costUsd"
                  stroke="hsl(var(--primary))"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4, strokeWidth: 0, fill: 'hsl(var(--primary))' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function AgentBreakdown({ rows }: { rows: UsageByAgent[] }) {
  const sorted = useMemo(() => [...rows].sort((a, b) => b.costUsd - a.costUsd), [rows]);
  const totalCost = useMemo(() => sorted.reduce((sum, r) => sum + r.costUsd, 0), [sorted]);
  return (
    <Card>
      <CardHeader>
        <CardTitle>Per-agent breakdown</CardTitle>
        <CardDescription>
          Sorted by cost — useful for spotting an agent burning more than its share before the
          next invoice lands.
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        {sorted.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground">No agent activity in this period.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Agent</TableHead>
                <TableHead className="text-right">Runs</TableHead>
                <TableHead className="text-right">Input</TableHead>
                <TableHead className="text-right">Output</TableHead>
                <TableHead className="text-right">Cost</TableHead>
                <TableHead className="text-right">Share</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((r) => {
                const share = totalCost > 0 ? (r.costUsd / totalCost) * 100 : 0;
                const agentId = r.agentId ?? '';
                return (
                  <TableRow key={agentId || r.agentName}>
                    <TableCell>
                      {agentId ? (
                        <Link
                          to={`/agents/${agentId}`}
                          className="font-medium underline-offset-4 hover:underline"
                        >
                          {r.agentName}
                        </Link>
                      ) : (
                        <span className="font-medium">{r.agentName}</span>
                      )}
                      {agentId && (
                        <div className="font-mono text-[11px] text-muted-foreground">{agentId}</div>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {r.runs.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {r.inputTokens.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {r.outputTokens.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums">
                      {formatUsd(r.costUsd)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">
                      {share.toFixed(0)}%
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
